/**
 * JitsiMeetingFrame — embeds a Jitsi meeting via the external iframe API and
 * bridges Remote commands into it. Ported from spot-client's JitsiMeetingFrame:
 * the iframe is created with `iAmSpot: true` + `toolbarButtons: []` (Spot hides
 * Jitsi's own toolbar and drives it via commands), and the same event listeners
 * feed presence back. Redux/enzyme/webpack are dropped — this is a lean React 18
 * component — but the command bridge ({@link applyMeetingCommand}) and the
 * iframe-API event handling carry over.
 *
 * Spaces does NOT rewrite Jitsi: in dev the TV joins anonymous alpha.jitsi.net
 * (no jwt — the dev meetingJwt won't validate there). The JWT path is config-ready
 * (`useMeetingJwt`).
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { ChatMessage, CommandName, MeetingParticipant, SpotTvPresence } from '@spaces/contracts';
import type { SET_RECORDING } from '../commands.js';
import { log } from '../log.js';
import { loadExternalApi } from './external-api.js';
import {
    applyMeetingCommand,
    initialToggleState,
    type CommandResult,
    type JitsiApi,
    type MeetingToggleState
} from './meeting-commands.js';

const DEFAULT_DISPLAY_NAME = 'Meeting Room';

/** Keep the relayed chat thread small: last N messages, each capped to M chars. */
const CHAT_THREAD_MAX = 50;
const CHAT_TEXT_MAX = 1000;

/**
 * The iframe's lib-jitsi-meet `ProxyConnectionService` resolves the incoming
 * screenshare track's SSRC OWNER from the peer "jid" via
 * `Strophe.getResourceFromJid(remoteJid)` — which returns null unless the jid has
 * a `/resource`. Our DO endpoint ids are bare strings, so the owner came back
 * empty and the TV dropped the track ("No SSRC owner known … track creation
 * failed"). Wrap the bare id as a pseudo-JID with the id as BOTH node and resource
 * when handing it to the iframe, and unwrap it back to the bare id for DO routing.
 * The synthetic domain is never resolved — only the resource (the owner) matters.
 */
const PROXY_JID_DOMAIN = 'proxy.spaces.invalid';

function toProxyJid(endpointId: string): string {
    return `${endpointId}@${PROXY_JID_DOMAIN}/${endpointId}`;
}

function fromProxyJid(jid: string | undefined): string {
    if (!jid) {
        return '';
    }
    const slash = jid.indexOf('/');

    return slash >= 0 ? jid.slice(slash + 1) : jid;
}

/**
 * Build the room name the iframe actually joins. 8x8 addresses a tenant-scoped
 * conference as `<tenant>/<room>` in the room name (the iframe splits the leading
 * path segment off as the tenant). A non-empty tenant routes the meeting into the
 * customer's environment (so server-side recording persists there); an empty tenant
 * yields a flat, anonymous room name.
 */
function tenantScopedRoomName(tenant: string, meetingName: string): string {
    return tenant ? `${tenant}/${meetingName}` : meetingName;
}

export interface JitsiMeetingFrameProps {
    /** Jitsi meeting domain (config: alpha.jitsi.net in dev). */
    meetingDomain: string;
    /**
     * The 8x8 meeting TENANT NAME to join under. When non-empty the iframe joins
     * `<meetingTenant>/<meetingName>` (8x8's `<domain>/<tenant>/<room>` addressing)
     * so the conference lives in the customer's own meeting environment and
     * server-side recording persists there. Empty => a flat/anonymous join. This is
     * the URL-path tenant slug (e.g. "emchodemo"), NOT the JWT `cid`/customer id.
     */
    meetingTenant: string;
    /** URL of external_api.js to load at runtime. */
    externalApiSrc: string;
    /** The bare meeting/conference name to join (the tenant prefix is applied here). */
    meetingName: string;
    /**
     * Phone invitees to dial into the meeting on join (the Remote's "Dial a number" /
     * "Join with ID + phone"). On `videoConferenceJoined` the TV calls the Jitsi iframe
     * `invite(invitees)` method, which routes each `{type:'phone'}` to jigasi (the SIP/
     * PSTN gateway) to place the outbound call. Requires the meeting JWT's outbound-call
     * feature (granted on real 8x8.vc). Empty => no dial-out.
     */
    invites?: { type: 'phone'; number: string }[];
    /** Self-signed meetingJwt; passed to the iframe only when `useJwt` is true. */
    jwt?: string;
    /** Whether to pass the jwt (false in dev → anonymous alpha join). */
    useJwt: boolean;
    /** TV display name in the meeting. */
    displayName?: string;
    /** Partial presence updates as the meeting state changes (TV broadcasts these). */
    onPresence: (partial: Partial<SpotTvPresence>) => void;
    /** Invoked when the meeting ends (readyToClose / hangup-with-skipFeedback). */
    onMeetingEnd: () => void;
    /**
     * The iframe wants to send a wireless-screenshare proxy signalling message OUT
     * to the sharing laptop. The iframe emits `{ to, data }` (spot's
     * `_onSendMessageToRemoteControl({ to, data })` shape): `to` is the sharer's
     * endpoint id, `data` is the inner `{ iq }` to relay. The TV app forwards the
     * INNER `data` to that sharer over the DO (`MESSAGES.JITSI_MEET_UPDATE`); the
     * sharer's `processMessage` then wraps it back as `{ from, data }`. See
     * {@link feedProxyMessage} for the inbound direction.
     */
    onProxyConnectionEvent?: (event: { to?: string; data?: unknown }) => void;
}

/** Imperative handle: the TV app calls `applyCommand` when a command frame arrives. */
export interface JitsiMeetingFrameHandle {
    applyCommand(command: CommandName | typeof SET_RECORDING, payload: unknown): CommandResult;
    /**
     * Feed an inbound wireless-screenshare proxy message (from the sharing laptop,
     * relayed over the DO as `MESSAGES.CLIENT_PROXY_MESSAGE`) into the meeting via
     * the iframe's `sendProxyConnectionEvent`. Mirrors spot's TV-side proxy pump.
     *
     * lib-jitsi-meet's `ProxyConnectionService.processMessage` REQUIRES the full
     * `{ from, data: { iq } }` envelope — it does `const t = e.from; if (!t) return`
     * and reads `e.data.iq`. `from` (the sharer's endpoint id) is the message's
     * sender; `data` is the inner `{ iq }`. Dropping `from` makes the TV silently
     * ignore the INITIATE and the screenshare never connects.
     */
    feedProxyMessage(from: string, data: unknown): void;
}

export const JitsiMeetingFrame = forwardRef<JitsiMeetingFrameHandle, JitsiMeetingFrameProps>(
    function JitsiMeetingFrame(props, ref) {
        const containerRef = useRef<HTMLDivElement | null>(null);
        const apiRef = useRef<JitsiApi | null>(null);
        const stateRef = useRef<MeetingToggleState>(initialToggleState());
        const localIdRef = useRef<string>('');
        const localRoleRef = useRef<string>('');
        /** REAL Jitsi meeting participants (remotes only; the local TV is added at broadcast). */
        const participantsRef = useRef<Map<string, { displayName?: string; role?: string }>>(new Map());
        /** Bounded recent chat thread relayed to Remotes so the controller can read it. */
        const chatThreadRef = useRef<ChatMessage[]>([]);
        /** Monotonic counter for synthesizing stable chat-message ids (events lack a usable one). */
        const chatSeqRef = useRef<number>(0);

        useImperativeHandle(ref, () => ({
            applyCommand(command, payload): CommandResult {
                const api = apiRef.current;

                if (!api) {
                    return { ok: false, error: 'meeting not ready' };
                }

                log.log('TV applyCommand', { command, payload });

                return applyMeetingCommand(
                    api,
                    stateRef.current,
                    command,
                    (payload ?? {}) as Record<string, unknown>,
                    props.onMeetingEnd
                );
            },
            feedProxyMessage(from, data): void {
                const api = apiRef.current;

                if (api?.sendProxyConnectionEvent) {
                    log.log('TV feeding proxy message into iframe', { from });
                    // The iframe's ProxyConnectionService.processMessage needs the
                    // full { from, data } envelope (spot's TV-side shape) — `from`
                    // identifies the peer, `data` is the inner { iq }. Shape `from`
                    // as a pseudo-JID so the iframe can resolve the screenshare
                    // track's SSRC owner (see toProxyJid).
                    api.sendProxyConnectionEvent({ from: toProxyJid(from), data });
                } else {
                    log.warn('TV cannot feed proxy message — iframe not ready');
                }
            }
        }), [props.onMeetingEnd]);

        useEffect(() => {
            let disposed = false;
            let api: (JitsiApi & {
                addListener(event: string, handler: (payload: never) => void): void;
                dispose(): void;
            }) | null = null;

            const { onPresence, onMeetingEnd, onProxyConnectionEvent } = props;
            const toggle = stateRef.current;

            loadExternalApi(props.externalApiSrc)
                .then(() => {
                    if (disposed || !containerRef.current || !window.JitsiMeetExternalAPI) {
                        return;
                    }

                    onPresence({ view: 'meeting', inMeeting: props.meetingName });

                    const instance = new window.JitsiMeetExternalAPI(props.meetingDomain, {
                        roomName: tenantScopedRoomName(props.meetingTenant, props.meetingName),
                        parentNode: containerRef.current,
                        jwt: props.useJwt ? props.jwt : undefined,
                        configOverwrite: {
                            iAmSpot: true,
                            disableDeepLinking: true,
                            prejoinConfig: { enabled: false },
                            toolbarButtons: [],
                            startWithVideoMuted: false
                        },
                        interfaceConfigOverwrite: {
                            SHOW_CHROME_EXTENSION_BANNER: false
                        }
                    });

                    api = instance;
                    apiRef.current = instance;

                    const on = (event: string, handler: (p: never) => void): void =>
                        instance.addListener(event, handler);

                    const displayName = props.displayName ?? DEFAULT_DISPLAY_NAME;

                    // Build the REAL meeting roster the TV sees (local TV first, then
                    // remote participants) and broadcast it so every Remote renders the
                    // actual call participants — NOT the control-plane occupancy, and
                    // never the Remote itself (a Remote is not a Jitsi participant).
                    const broadcastParticipants = (): void => {
                        const list: MeetingParticipant[] = [];

                        if (localIdRef.current) {
                            list.push({
                                participantId: localIdRef.current,
                                displayName,
                                role: localRoleRef.current || undefined,
                                local: true
                            });
                        }
                        for (const [id, info] of participantsRef.current) {
                            list.push({ participantId: id, displayName: info.displayName, role: info.role });
                        }
                        onPresence({ participants: list });
                    };

                    // Append a chat message to the bounded thread and broadcast it so
                    // every Remote can READ the conversation (incoming + the room's own
                    // outgoing), not just compose into it. Trimmed to the last N
                    // messages, each capped, to keep presence small.
                    const pushChatMessage = (msg: Omit<ChatMessage, 'id'> & { id?: string }): void => {
                        const text = (msg.text ?? '').slice(0, CHAT_TEXT_MAX);

                        if (!text) {
                            return;
                        }
                        chatSeqRef.current += 1;
                        const entry: ChatMessage = {
                            id: msg.id || `c${chatSeqRef.current}`,
                            from: msg.from || 'Guest',
                            text,
                            me: msg.me,
                            private: msg.private,
                            timestamp: msg.timestamp ?? new Date().toISOString()
                        };

                        chatThreadRef.current = [...chatThreadRef.current, entry].slice(-CHAT_THREAD_MAX);
                        onPresence({ chatThread: chatThreadRef.current });
                    };

                    on('videoConferenceJoined', (({ id }: { id: string }) => {
                        localIdRef.current = id;
                        log.log('TV meeting joined', { id, meeting: props.meetingName });
                        onPresence({ view: 'meeting', inMeeting: props.meetingName, waitingForMeetingStart: false });

                        // Dial out any phone invitees (the Remote's "Dial a number"). `invite`
                        // is a DIRECT iframe-API method (not executeCommand); each {type:'phone'}
                        // routes to jigasi (SIP/PSTN) to place the call. Needs the meeting JWT's
                        // outbound-call feature (real 8x8.vc). No-op when there are none.
                        if (props.invites && props.invites.length > 0) {
                            log.log('TV dialing out invitees', { count: props.invites.length });
                            (instance as unknown as { invite?: (invitees: unknown[]) => void })
                                .invite?.(props.invites);
                        }

                        // Seed any participants already present before the TV joined, then
                        // broadcast the initial roster (at least the TV itself).
                        Promise.resolve(
                            (instance as unknown as { getParticipantsInfo?: () => unknown }).getParticipantsInfo?.()
                        )
                            .then(infos => {
                                if (Array.isArray(infos)) {
                                    for (const p of infos as Array<Record<string, unknown>>) {
                                        const pid = String(p.participantId ?? p.id ?? '');

                                        if (pid && pid !== localIdRef.current) {
                                            participantsRef.current.set(pid, {
                                                displayName: (p.displayName ?? p.formattedDisplayName) as string | undefined,
                                                role: p.role as string | undefined
                                            });
                                        }
                                    }
                                }
                            })
                            .catch(() => { /* getParticipantsInfo unsupported — events still drive the roster */ })
                            .finally(() => broadcastParticipants());
                    }) as (p: never) => void);

                    on('videoConferenceLeft', (() => {
                        log.log('TV meeting left');
                        participantsRef.current.clear();
                        chatThreadRef.current = [];
                        localIdRef.current = '';
                        localRoleRef.current = '';
                        onPresence({ inMeeting: '', participants: [], chatThread: [], chatPanelOpen: false });
                    }) as (p: never) => void);

                    on('participantJoined', (({ id, displayName: name }: { id: string; displayName?: string }) => {
                        participantsRef.current.set(id, {
                            ...participantsRef.current.get(id),
                            displayName: name
                        });
                        broadcastParticipants();
                    }) as (p: never) => void);

                    on('participantLeft', (({ id }: { id: string }) => {
                        participantsRef.current.delete(id);
                        broadcastParticipants();
                    }) as (p: never) => void);

                    on('participantKicked', (({ kicked }: { kicked?: { id?: string } }) => {
                        if (kicked?.id) {
                            participantsRef.current.delete(kicked.id);
                            broadcastParticipants();
                        }
                    }) as (p: never) => void);

                    on('displayNameChange', (({ id, displayname }: { id: string; displayname?: string }) => {
                        if (id === localIdRef.current) {
                            return; // the TV's own name is authoritative (set below)
                        }
                        participantsRef.current.set(id, {
                            ...participantsRef.current.get(id),
                            displayName: displayname
                        });
                        broadcastParticipants();
                    }) as (p: never) => void);

                    on('readyToClose', (() => onMeetingEnd()) as (p: never) => void);

                    on('audioMuteStatusChanged', (({ muted }: { muted: boolean }) => {
                        log.log('TV audioMuteStatusChanged', { from: toggle.audioMuted, to: muted, intent: toggle.audioMuteIntent });
                        toggle.audioMuted = muted;

                        // The TV exposes NO local mic control (toolbarButtons: []), so the only
                        // legitimate source of an audio-mute change is a Remote `setAudioMute`,
                        // which records `audioMuteIntent`. A reported state that contradicts a
                        // set intent is a spurious echo — Jitsi recreating the local audio track
                        // unmuted during a renegotiation (the same flicker we see for video on
                        // join). Re-assert the intent on the real track (so the room stays truly
                        // muted) and do NOT let the transient clobber the latched presence.
                        if (toggle.audioMuteIntent !== null && muted !== toggle.audioMuteIntent) {
                            log.warn('TV audio-mute drifted from intent — re-asserting', {
                                reported: muted, intent: toggle.audioMuteIntent
                            });
                            instance.executeCommand('toggleAudio');

                            return;
                        }

                        onPresence({ audioMuted: muted });
                    }) as (p: never) => void);

                    on('videoMuteStatusChanged', (({ muted }: { muted: boolean }) => {
                        log.log('TV videoMuteStatusChanged', { from: toggle.videoMuted, to: muted });
                        toggle.videoMuted = muted;
                        onPresence({ videoMuted: muted });
                    }) as (p: never) => void);

                    on('tileViewChanged', (({ enabled }: { enabled: boolean }) => {
                        toggle.tileView = enabled;
                        onPresence({ tileView: enabled });
                    }) as (p: never) => void);

                    on('screenSharingStatusChanged', (({ on: sharing }: { on?: boolean }) => {
                        toggle.screensharing = Boolean(sharing);
                        onPresence({ screensharing: Boolean(sharing) });
                    }) as (p: never) => void);

                    on('raiseHandUpdated', (({ id, handRaised }: { id: string; handRaised: number }) => {
                        if (id !== localIdRef.current) {
                            return;
                        }
                        toggle.handRaised = Boolean(handRaised);
                        onPresence({ handRaised: Boolean(handRaised) });
                    }) as (p: never) => void);

                    on('participantRoleChanged', (({ id, role }: { id: string; role: string }) => {
                        if (localIdRef.current && id === localIdRef.current) {
                            localRoleRef.current = role;

                            const isLocalModerator = role === 'moderator';

                            log.log('TV role changed', { role, isLocalModerator });
                            onPresence({ isLocalModerator });
                            broadcastParticipants();

                            return;
                        }

                        // A remote participant's role changed — reflect it in the roster.
                        const existing = participantsRef.current.get(id);

                        if (existing) {
                            participantsRef.current.set(id, { ...existing, role });
                            broadcastParticipants();
                        }
                    }) as (p: never) => void);

                    on('passwordRequired', (() => onPresence({ needPassword: true })) as (p: never) => void);

                    on('recordingStatusChanged', (({ on: rec }: { on?: boolean }) => {
                        toggle.recording = Boolean(rec);
                        onPresence({ recording: Boolean(rec) });
                    }) as (p: never) => void);

                    // Chat is read ON THE TV. The iframe reports the panel's real
                    // open/closed state via `chatUpdated` (`isOpen`); track it so a
                    // no-arg `toggleChat` only ever OPENS a closed panel (see
                    // applyMeetingCommand SEND_CHAT_MESSAGE) and never hides an open one.
                    // Relay it in presence too so the Remote can render an accurate
                    // open/close toggle for the room's chat panel.
                    on('chatUpdated', (({ isOpen }: { isOpen?: boolean }) => {
                        toggle.chatOpen = Boolean(isOpen);
                        onPresence({ chatPanelOpen: Boolean(isOpen) });
                    }) as (p: never) => void);

                    // A participant sent a chat message — open the panel so the ROOM can
                    // read incoming chat on the big screen, AND relay the message into
                    // presence so the Remote controller can read the thread too (not just
                    // send into it). The iframe event carries `nick`/`from` (sender),
                    // `message` (text) and `privateMessage`.
                    on('incomingMessage', ((e: {
                        from?: string; nick?: string; message?: string; privateMessage?: boolean;
                    }) => {
                        if (!toggle.chatOpen) {
                            instance.executeCommand('toggleChat');
                            toggle.chatOpen = true;
                        }
                        const fromId = String(e?.from ?? '');
                        const name = (e?.nick ?? '').trim()
                            || participantsRef.current.get(fromId)?.displayName
                            || 'Guest';

                        pushChatMessage({
                            from: name,
                            text: String(e?.message ?? ''),
                            private: Boolean(e?.privateMessage)
                        });
                    }) as (p: never) => void);

                    // The room itself sent a chat message (a Remote relayed
                    // `sendChatMessage` through the TV). Mirror it into the thread so the
                    // Remote sees both sides of the conversation. `outgoingMessage`
                    // carries `message` + `privateMessage` (no sender — it's the TV).
                    on('outgoingMessage', ((e: { message?: string; privateMessage?: boolean }) => {
                        pushChatMessage({
                            from: displayName,
                            text: String(e?.message ?? ''),
                            me: true,
                            private: Boolean(e?.privateMessage)
                        });
                    }) as (p: never) => void);

                    // Wireless screenshare: the iframe emits `{ to, data }` to send OUT
                    // to the sharing laptop. Pass the whole event up; the TV app
                    // forwards the inner `data` to the sharer named by `to`.
                    on('proxyConnectionEvent', ((event: { to?: string; data?: unknown }) => {
                        // The iframe targets the pseudo-JID we fed it — unwrap back to
                        // the bare DO endpoint id so the room DO can route the reply.
                        const to = fromProxyJid(event?.to);

                        log.log('TV proxyConnectionEvent (outbound)', { to });
                        onProxyConnectionEvent?.({ to, data: event?.data });
                    }) as (p: never) => void);

                    instance.executeCommand('displayName', props.displayName ?? DEFAULT_DISPLAY_NAME);
                })
                .catch(err => log.error('meeting frame failed to init', { error: String(err) }));

            return () => {
                disposed = true;
                try {
                    api?.dispose();
                } catch {
                    // already torn down
                }
                apiRef.current = null;
                stateRef.current = initialToggleState();
            };
        }, [props.meetingName]);

        return <div className = "meeting-frame" ref = { containerRef } />;
    }
);
